<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productProfile.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productProfile.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <ts-button class="ts-capitalize" outline @click.prevent="allSerial">
              {{ $t('productProfile.ViewAllSerial') }}
            </ts-button>
          </div>
          <div class="d-flex">
            <ts-input
              v-tooltip="$t('productProfile.search')"
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px;"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'product_category.category_name_en'
          }"
        >
          <template v-slot="{ record }">
            <td>{{ record.barcode }}</td>
            <td>{{ record.group ? record.group.pro_group_name_en : null }}</td>
            <td>{{ record.product_name_kh }}</td>
            <td>{{ record.product_name_en }}</td>
            <td>{{ record.uom_category.uom_cat_name_en }}</td>
            <td>{{ record.type.product_type_en }}</td>
            <td>
              <table class="vendor-list">
                <tr v-for="(item, index) in record.vendors" :key="index">
                  <td>{{ item.vendor_name }}</td>
                </tr>
              </table>
            </td>
            <td class="ts-text-center">
              <span class="badge badge-success" v-if="record.is_expirable"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
            </td>
            <td class="ts-text-center">
              <span class="badge badge-success" v-if="record.is_warranty"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
            </td>
            <td class="ts-text-center">
              <a-switch
                v-model="record.is_available_for_sale"
                size="small"
                :loading="record.loading_sale"
                @change="allowForSale(record)"
              />
            </td>
            <td class="ts-text-center">
              <a-switch
                v-model="record.is_calculate_stock"
                size="small"
                :loading="record.loading_stock"
                @change="calculate(record)"
              />
            </td>
            <td class="text-center ts-space-x-3 ts-flex">
              <a
                href="#"
                v-tooltip="$t('productProfile.detail')"
                @click.prevent="viewDetail(record)"
              >
                <i class="fas fa-file"></i>
              </a>
              <a
                v-if="record.is_generate_unique_serial"
                href="#"
                v-tooltip="$t('productProfile.viewSerial')"
                class="text-primary"
                @click.prevent="viewSeril(record)"
              >
                <i class="fas fa-list-ul"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'profileIndex',
  data() {
    return {
      loading: false,
      showForm: false
    }
  },
  computed: {
    ...mapState('product/profile', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.product.profile.search
      },
      set(newValue) {
        this.$store.commit('product/profile/SET_SEARCH', newValue)
        this.$store.commit('product/profile/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        { name: this.$t('productProfile.barcode'), style: 'width: 7%' },
        { name: this.$t('productProfile.group'), style: 'width: 8%' },
        { name: this.$t('productProfile.nameKh'), style: 'width: 17%' },
        { name: this.$t('productProfile.nameEn'), style: 'width: 17%' },
        { name: this.$t('productProfile.uomCategory') },
        { name: this.$t('productProfile.productType'), style: 'width: 10%' },
        { name: this.$t('productProfile.supplier'), style: 'width: 10%' },
        {
          name: this.$t('productProfile.expirationAvailable'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productProfile.isWarranty'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productProfile.saleAvailable'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productProfile.calculateStock'),
          style: 'text-align:center'
        },
        { name: this.$t('actions'), style: 'text-align:center; width:80px' }
      ]
    }
  },
  methods: {
    ...mapActions('product/profile', ['saleAvailable', 'calculateStock']),
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('product/profile/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.$router.push({ name: 'product-profile-create' })
    },
    edit(record) {
      this.$store.commit('product/profile/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'product-profile-edit',
        params: { id: record.product_id }
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('product/profile/destroy', record.product_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    async allowForSale(record) {
      record.loading_sale = true
      let response = await this.saleAvailable(record.product_id)
      record.is_available_for_sale = response.is_available_for_sale
      record.loading_sale = false
    },
    async calculate(record) {
      record.loading_stock = true
      let response = await this.calculateStock(record.product_id)
      record.is_calculate_stock = response.is_calculate_stock
      record.loading_stock = false
    },
    viewSeril(record) {
      this.$router.push({
        name: 'product-profile-serail',
        params: { id: record.product_id }
      })
    },
    allSerial() {
      this.$router.push({ name: 'product-profile-all-serail' })
    },
    viewDetail(record) {
      this.$router.push({
        name: 'product-detail-info',
        params: { id: record.product_id }
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('product/profile/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
<style scoped>
.vendor-list {
  display: table;
  border-collapse: collapse;
  border: none;
}
.vendor-list td {
  padding: 2px 4px;
  border-top: none !important;
  font-size: 10px;
}
</style>
